import {
  createBrowserRouter,
  Outlet,
  Navigate,
} from "react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "../lib/queryClient";
import { LEGACY_LOGIN_PATHS } from "./legacyLoginPaths";

import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { RequireAuth, ValidateAuth, PreventAuthAccess } from "../features/auth/guards";
import { useAuth } from "@features/auth";
import { ActiveHospitalProvider } from "@features/hospitals";
import { ActiveConvenioProvider } from "../contexts/ActiveConvenio";
import { ReportModalProvider } from "@features/reports";
import { AppProvider } from "../AppProvider";

import Aside from "../components/feature/Aside";
import { ErrorBoundary } from "../components/feature/ErrorBoundary";
import { ModalComponent } from "../components/ui/Modal";
import { PrimaryButton } from "../components/ui/Buttons";

import styled from "styled-components";
import { theme } from "../styles/theme";
import { ROLE_CONVENIO, ROLE_DOCTOR, ROLE_HOSPITALS, ROLE_PATIENT } from "../types/role";

// =============================================================================
// LAZY LOADED PAGES - Code Splitting for better performance
// =============================================================================

// Public pages
const Privacy = lazy(() => import("../pages/privacy"));
const LoginLayout = lazy(() => import("../pages/login/LoginLayout"));
const UnifiedLogin = lazy(() => import("../pages/login/UnifiedLogin"));
const PatientCpfSearch = lazy(() => import("../pages/login/paciente/PatientCpfSearch"));
const PatientOtpStep = lazy(() => import("../pages/login/paciente/PatientOtpStep"));
const PatientBirthdate = lazy(() => import("../pages/login/paciente/PatientBirthdate"));

// Registration pages
const Register = lazy(() => import("../pages/Register/index"));
const RegisterPatient = lazy(() => import("../pages/Register/patient/PatientRegister/patientRegister"));
const RegisterDoctor = lazy(() => import("../pages/Register/doctor/DoctorStepOne/doctorStepOne"));
const RegisterHospital = lazy(() => import("../pages/Register/hospital/HospitalStepOne/hospitalStepOne"));
const RegisterConvenio = lazy(() => import("../pages/Register/convenio/ConvenioStepOne/convenioStepOne"));
const ValidatePatient = lazy(() => import("../pages/Register/patient/PatientValidate/patientValidate"));
const InsertEmail = lazy(() => import("../pages/Register/patient/InsertEmail"));
const InsertCellphone = lazy(() => import("../pages/Register/patient/InsertCellphone"));
const ValidateSMSPage = lazy(() => import("../pages/ValidateSMS"));
const RegistrationApproved = lazy(() => import("../pages/Register/patient/RegistrationApproved/registrationApproved"));
const DoctorRegistrationRequested = lazy(() => import("../pages/Register/doctor/DoctorRegistrationRequested/doctorRegistrationRequested"));
const HospitalUserRegistrationRequested = lazy(() => import("../pages/Register/hospital/HospitalUserRegistrationRequested/hospitalUserRegistrationRequested"));

// Authenticated pages
const HomeComposed = lazy(() => import("../pages/inicio"));
const Exams = lazy(() => import("../pages/Exams"));
const DetailExam = lazy(() => import("../pages/Exams/Details"));
const Users = lazy(() => import("../pages/Users"));
const AddUser = lazy(() => import("../pages/Users/AddUser/AddUser"));
// Spec 20261028-132000 AC-8: one page per user with tabs; the old edit and audit routes open it on their tab.
const UserPage = lazy(() => import("../pages/Users/UserPage"));
const UserTabRedirect = lazy(() =>
  import("../pages/Users/UserPage").then((module) => ({ default: module.UserTabRedirect })),
);
const UserAudit = lazy(() => import("../pages/Users/Audit/UserAudit"));
const MyProfile = lazy(() => import("../pages/MyProfile"));
const Observability = lazy(() => import("../pages/Observability"));
const Unidades = lazy(() => import("../pages/Unidades"));
const Financeiro = lazy(() => import("../pages/Financeiro"));
const Report = lazy(() => import("../pages/Report").then(m => ({ default: m.Report })));
const Occurrence = lazy(() => import("../pages/Occurrence"));
const GridAttendance = lazy(() => import("../components/feature/GridAttendance"));

// Internal Doctors pages
const InternalDoctorsForm = lazy(() => import("../pages/InternalDoctors"));
const InternalDoctorsTemplates = lazy(() => import("../pages/InternalDoctorsTemplates"));
const InternalDoctorDocuments = lazy(() => import("../pages/InternalDoctorsDocuments/InternalDoctorsDocuments"));
const InternalDoctorsManagement = lazy(() => import("../pages/InternalDoctorsManagement"));

// Addition Request pages
const AdditionRequest = lazy(() => import("../pages/AdditionRequest").then(m => ({ default: m.AdditionRequest })));
// Configuração do motor novo (spec 006, T047): recusas e regras de acréscimo.
const RetiradaNova = lazy(() => import("../pages/RetiradaNova").then(m => ({ default: m.RetiradaNova })));
const SelectTypeRequest = lazy(() => import("../pages/AdditionRequest/SelectTypeRequest").then(m => ({ default: m.SelectTypeRequest })));
// Motor novo (spec 006). Convive com o wizard, que continua no ar.
const AcrescimoNovoPage = lazy(() => import("../pages/AcrescimoNovo"));
const AdditionManagement = lazy(() => import("../pages/AdditionManagement").then(m => ({ default: m.AdditionManagement })));
const AcrescimoDetail = lazy(() => import("../pages/AcrescimoDetail"));

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const ModalContainer = styled.div`
  padding: 0rem 3rem;
  h1 {
    text-align: center;
    margin-bottom: 2.5rem;
    font-size: ${theme.fontSize["4xl"]};
    font-weight: 600;
  }
  p {
    margin-bottom: 2.5rem;
    font-size: ${theme.fontSize["2xl"]};
  }
`;

// =============================================================================
// AUTH GUARDS — Importados de src/features/auth/guards
// =============================================================================


// `fallback`: com a flag desligada, a rota de cadastro caía no login sem explicação.
// Quem tentou criar conta volta para a escolha de perfil, onde o convênio some.
function ConvenioRouteGuard({
  children,
  fallback = "/login",
}: {
  children: ReactNode;
  fallback?: string;
}) {
  const { enabled, loading } = useFeatureFlag("habilitar_convenio");

  if (loading) return <RouteLoader message="Verificando acesso..." />;
  if (!enabled) return <Navigate to={fallback} replace />;
  return <>{children}</>;
}
// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <Outlet />
      </AppProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

function PublicLayout() {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Outlet />
    </Suspense>
  );
}

const PlaceholderDateTime = () => {
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setDate(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const formattedTime = new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

  const formattedDay = new Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);

  return (
    <Datetime>
      <span>{formattedDay} · {formattedTime}</span>
    </Datetime>
  );
};

function AuthenticatedLayout() {
  const { isExpiredTokenModalOpen, setIsExpiredTokenModalOpen, logout } = useAuth();

  const closeModal = () => {
    logout();
    setIsExpiredTokenModalOpen(false);
  };

  return (
    <RequireAuth>
      <ActiveHospitalProvider>
        <ActiveConvenioProvider>
          <ReportModalProvider>
            <Aside customPlaceholder={
              <PlaceholderDateTime />
            }>
              <Suspense fallback={<RouteLoader message="Carregando página..." />}>
                <Outlet />
              </Suspense>
              {isExpiredTokenModalOpen && (
                <ModalComponent closeModal={closeModal} label="Sessão expirada">
                  <ModalContainer>
                    <h1>Atenção</h1>
                    <p>Sua sessão expirou. Por favor, faça login novamente.</p>
                    <PrimaryButton onClick={closeModal}>OK</PrimaryButton>
                  </ModalContainer>
                </ModalComponent>
              )}
            </Aside>
          </ReportModalProvider>
        </ActiveConvenioProvider>
      </ActiveHospitalProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </RequireAuth>
  );
}

// =============================================================================
// ROUTE CONFIGURATION (imported from constants for reuse)
// =============================================================================

import {
  ROLE_INTERNAL_DOCTOR,
  ROLE_OPERATION,
} from "../types/role";
import {
  PERMISSION_ADMINISTRATOR,
  PERMISSION_REPORTS,
  PERMISSION_RESULT,
} from "../types/permissions";
import { Datetime } from "../components/feature/Aside/style";
import RouteLoader from "../components/ui/RouteLoader";
import { ErpRedirect } from "../components/feature/ErpRedirect";
import { ERP_PATHS } from "@lib/erpUrl";

// =============================================================================
// ROUTER DEFINITION
// =============================================================================

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
// Public routes (PreventAuthAccess redireciona usuários logados)
{
  element: <PublicLayout />,
  children: [
    // Spec 20261028-130500 AC-1 (owner, 2026-10-28): the landing is hidden, its code kept. `/login` sends whoever has a
    // session to /inicio (PreventAuthAccess), so this one redirect covers both.
    { path: "/", element: <Navigate to="/login" replace /> },
    { path: "/politica-de-privacidade", element: <Privacy /> },
    {
      path: "/login",
      element: <PreventAuthAccess><LoginLayout /></PreventAuthAccess>,
      children: [
        { index: true, element: <UnifiedLogin /> },
        { path: "paciente", element: <PatientCpfSearch /> },
        { path: "paciente/birthdate", element: <PatientBirthdate /> },
        { path: "paciente/login-sms", element: <PatientOtpStep /> },
      ],
    },
    ...LEGACY_LOGIN_PATHS.map((path) => ({ path, element: <Navigate to="/login" replace /> })),
    // Cadastro e validação sob LoginLayout (D-3 redesigned):
    {
      element: <PreventAuthAccess><LoginLayout /></PreventAuthAccess>,
      children: [
        { path: "/cadastro", element: <Register /> },
        { path: "/cadastro/paciente", element: <RegisterPatient /> },
        { path: "/cadastro/paciente/email", element: <InsertEmail /> },
        { path: "/cadastro/paciente/cellphone", element: <InsertCellphone /> },
        { path: "/validar/paciente", element: <ValidatePatient /> },
        { path: "/validar/numero", element: <ValidateSMSPage /> },
        { path: "/cadastro-aprovado", element: <RegistrationApproved /> },
        { path: "/cadastro-solicitado-medico", element: <DoctorRegistrationRequested /> },
        { path: "/cadastro-solicitado", element: <HospitalUserRegistrationRequested /> },
        { path: "/cadastro/medico", element: <RegisterDoctor /> },
        { path: "/cadastro/hospital", element: <RegisterHospital /> },
        {
          path: "/cadastro/convenio",
          element: (
            <ConvenioRouteGuard fallback="/cadastro">
              <RegisterConvenio />
            </ConvenioRouteGuard>
          ),
        },
      ],
    },
        ],
      },

      // Authenticated routes - Internal Doctors
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            path: "/formulario-interno",
            element: (
              <ValidateAuth roles={[ROLE_INTERNAL_DOCTOR]}>
                <InternalDoctorsForm />
              </ValidateAuth>
            ),
          },
          {
            path: "/doctors-templates",
            element: (
              <ValidateAuth roles={[ROLE_INTERNAL_DOCTOR]}>
                <InternalDoctorsTemplates />
              </ValidateAuth>
            ),
          },
          {
            path: "/documentos",
            element: (
              <ValidateAuth roles={[ROLE_INTERNAL_DOCTOR]}>
                <InternalDoctorDocuments />
              </ValidateAuth>
            ),
          },
        ],
      },

      // Authenticated routes - Operation
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            path: "/acrescimo",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <AdditionManagement />
              </ValidateAuth>
            ),
          },
          {
            // Spec 20261028-131000 AC-5, AC-8: one acréscimo by its address. The server answers the scope.
            path: "/acrescimo/:id",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <AcrescimoDetail />
              </ValidateAuth>
            ),
          },
          {
            path: "/doctors-templates/:user_id",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION]}>
                <InternalDoctorsManagement />
              </ValidateAuth>
            ),
          },
          // Fluxo de acréscimo/retirada. O gate de rota é UX — evita levar quem
          // não tem caminho legítimo a uma tela que só daria erro ou beco. A trava
          // que vale é a do servidor: quem chamar o endpoint direto tem de bater
          // na autorização do backend, não neste array.
          //
          // Caminhos legítimos (ver SelectTypeRequest): operação/hospital vão para
          // a tela v2 (/carlao/leitura, que é [1,2]), e a retirada só mostra o
          // aviso de que volta quando estiver no núcleo (spec 20261013-112823).
          // Paciente não vê nem cria acréscimo (dono, 2026-10-14). Médico não tem
          // destino nesse fluxo — deixá-lo entrar só produzia beco.
          {
            path: "/resultados-exames/:uuid/:id/addition",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS]}>
                <AdditionRequest />
              </ValidateAuth>
            ),
          },
          {
            path: "/resultados-exames/addition/select-type",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <SelectTypeRequest />
              </ValidateAuth>
            ),
          },
          {
            // A retirada compulsória on the core (spec 20261026-142200 AC-18). Operação, hospital staff and doctors
            // search and request; a patient and a convênio are refused by the server (AC-11).
            path: "/retirada/nova",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <RetiradaNova />
              </ValidateAuth>
            ),
          },
          {
            path: "/occurrence",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION]}>
                <Occurrence />
              </ValidateAuth>
            ),
          },
        ],
      },

      // Authenticated routes - Mixed roles
      {
        element: <AuthenticatedLayout />,
        children: [
          // Home composta única (ADR home-composta-inicio) — destino de entrada de todos
          // os papéis, EXCETO médico interno (vai para /formulario-interno). A exceção
          // era só comentário e por isso não valia: personificar role 5 pousava na home
          // vazia, sem link de saída (G7).
          {
            path: "/inicio",
            element: (
              <ValidateAuth excludeRoles={[ROLE_INTERNAL_DOCTOR]}>
                <HomeComposed />
              </ValidateAuth>
            ),
          },
          // P20EE-208: every signed-in role edits its own profile (name and contact consent).
          {
            path: "/meu-perfil",
            element: (
              <ValidateAuth>
                <MyProfile />
              </ValidateAuth>
            ),
          },
          {
            path: "/resultados",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR, PERMISSION_RESULT]}
              >
                <GridAttendance pageSize={10} />
              </ValidateAuth>
            ),
          },
          {
            path: "/relatorio/:report_id",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS]}
                permissions={[PERMISSION_ADMINISTRATOR, PERMISSION_REPORTS]}
                hospitalNeedsToBeActive
              >
                <Report />
              </ValidateAuth>
            ),
          },
          // Lista completa de exames do paciente/médico (destino do "ver todos" do bloco
          // "Meus exames" da home). Mantém filtros/paginação — a home mostra só o preview.
          {
            path: "/resultados-exames",
            element: (
              <ValidateAuth roles={[ROLE_PATIENT, ROLE_DOCTOR]}>
                <Exams />
              </ValidateAuth>
            ),
          },
          // Spec 20261105-090000 AC-8 (DEV-349): the patient's invoices. The server answers only a patient's session
          // (AC-11); the Operação, which this guard lets through by its superadmin bypass, meets the page's forbidden
          // answer instead.
          {
            path: "/financeiro",
            element: (
              <ValidateAuth roles={[ROLE_PATIENT]}>
                <Financeiro />
              </ValidateAuth>
            ),
          },
          // Dashboard legado consolidado na home composta — redirect de compatibilidade (ADR).
          {
            path: "/dashboard",
            element: <Navigate to="/inicio" replace />,
          },
          {
            // Redirect, não tela. O `Exams` NUNCA leu este `:uuid` (não tem
            // `useParams`): ele usa `activeHospital.uuid`. Resultado: a URL
            // dizia um hospital e a tela mostrava outro — silenciosamente, e
            // justamente para papel 2, que tem mais de um. Não era brecha (o
            // servidor recusa uuid alheio), era mentira de endereço.
            //
            // Ninguém navegava para cá: todos os links usam `/:uuid/:id` com o
            // atendimento (grep em MeusExamesPreview, GridAttendance/Result e
            // SelectAppointment). Só se chegava digitando. Redirect em vez de
            // remoção para não quebrar favorito.
            path: "/resultados-exames/:uuid",
            element: <Navigate to="/resultados-exames" replace />,
          },
          {
            path: "/resultados-exames/:uuid/:id",
            element: (
              <ValidateAuth
                roles={[ROLE_PATIENT, ROLE_DOCTOR, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR, PERMISSION_RESULT]}
              >
                <DetailExam />
              </ValidateAuth>
            ),
          },
        ],
      },

      // Authenticated routes - User management
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            // Spec 20261103-120000 (P20EE-209): the Operação sets each unit's type; the route admits no one else.
            path: "/unidades",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION]}>
                <Unidades />
              </ValidateAuth>
            ),
          },
          {
            path: "/users",
            element: (
              <ValidateAuth
                // Sem ROLE_CONVENIO: convênio não gerencia usuário (spec 007). Deixá-lo na lista
                // seria papel morto afirmando o contrário da regra — a permissão já o barra.
                roles={[ROLE_OPERATION, ROLE_HOSPITALS]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <Users />
              </ValidateAuth>
            ),
          },
          {
            // Tela de leitura v2 (spec 061), a partir de um atendimento do núcleo, pelo seu uuid (spec
            // 20261013-112823 AC-6). O uuid da sessão na URL faz o reload retomar a sessão.
            path: "/acrescimo/:attendanceUuid/leitura/:sessionId?",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <AcrescimoNovoPage />
              </ValidateAuth>
            ),
          },
          {
            // A mesma tela no modo geral: o "Novo" sem atendimento. Desde a onda 1 (spec 20261013-112823,
            // Clarificação 1) toda entrada do acréscimo abre esta tela; a flag `carlao_v2_reading` não é mais lida.
            path: "/carlao/leitura/:sessionId?",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <AcrescimoNovoPage />
              </ValidateAuth>
            ),
          },
          // Closing plan A5 (2026-10-29): the legacy wizard is gone; its old addresses open the reading screen.
          ...["/carlao/wizard", "/carlao/wizard/:sessionId", "/carlao/novo-acrescimo/:sessionId?"].map((path) => ({
            path,
            element: <Navigate to="/carlao/leitura" replace />,
          })),
          // Spec 20261015-160924 T12: the Carlão pages moved to the ERP (portal-senneliquor). The old addresses keep
          // their guard and send bookmarks there.
          {
            path: "/analises-carlao",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION]}
                permissions={[PERMISSION_ADMINISTRATOR]}
              >
                <ErpRedirect path={ERP_PATHS.carlaoAnalytics} />
              </ValidateAuth>
            ),
          },
          {
            path: "/configuracao-carlao",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION]}
                permissions={[PERMISSION_ADMINISTRATOR]}
              >
                <ErpRedirect path={ERP_PATHS.carlaoConfiguracao} />
              </ValidateAuth>
            ),
          },
          {
            path: "/users/add",
            element: (
              <ValidateAuth
                // Sem ROLE_CONVENIO: convênio não gerencia usuário (spec 007). Deixá-lo na lista
                // seria papel morto afirmando o contrário da regra — a permissão já o barra.
                roles={[ROLE_OPERATION, ROLE_HOSPITALS]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <AddUser />
              </ValidateAuth>
            ),
          },
          {
            path: "/users/:id",
            element: (
              <ValidateAuth
                // Sem ROLE_CONVENIO: convênio não gerencia usuário (spec 007). Deixá-lo na lista
                // seria papel morto afirmando o contrário da regra — a permissão já o barra.
                roles={[ROLE_OPERATION, ROLE_HOSPITALS]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <UserPage />
              </ValidateAuth>
            ),
          },
          {
            // Auditoria do usuário (spec 006, US3). Gate alinhado às rotas
            // irmãs de /users NA FLAG, não na lista de papéis:
            // - `hospitalNeedsToBeActive` entra porque a leitura é escopada a
            //   hospital, igual a perfil/edição — papel 2 sem procedência ativa
            //   não gere usuário, logo não lê a trilha deles;
            // - ROLE_CONVENIO (6) NÃO entra: `audit.routes.ts` gateia
            //   `users:manage` em [1, 2], então convênio na rota só produziria
            //   403 — abrir a porta da frente para o backend fechar a de trás é
            //   pior que negar aqui.
            path: "/users/:userId/auditoria",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <UserTabRedirect tab="atividade" />
              </ValidateAuth>
            ),
          },
          {
            // Auditoria por hospital (spec 006, FR-008) — mesma tela, mesmo
            // gate: o backend usa o MESMO `users:manage` [1, 2] nas duas rotas.
            path: "/hospitais/:hospitalId/auditoria",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <UserAudit />
              </ValidateAuth>
            ),
          },
          {
            // Observabilidade e auditoria de ACESSO (spec 008, US2 + US3). Gate pelo direito, `audit:read`, como o
            // servidor (`audit.routes.ts`): o payload de permissões traz o código além do id (P20EE-188, a T011b).
            path: "/observabilidade",
            element: (
              <ValidateAuth permissionCodes={["audit:read"]} hospitalNeedsToBeActive>
                <Observability />
              </ValidateAuth>
            ),
          },
          {
            path: "/users/:id/editar",
            element: (
              <ValidateAuth
                // Sem ROLE_CONVENIO: convênio não gerencia usuário (spec 007). Deixá-lo na lista
                // seria papel morto afirmando o contrário da regra — a permissão já o barra.
                roles={[ROLE_OPERATION, ROLE_HOSPITALS]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <UserTabRedirect tab="acessos" />
              </ValidateAuth>
            ),
          },
        ],
      },

      // Catch-all 404. `notFound` é obrigatório aqui: nesta posição o
      // ErrorBoundary não é boundary — é elemento normal, e `useRouteError()`
      // volta undefined, fazendo a tela dizer "Erro inesperado" para quem só
      // digitou um endereço errado.
      {
        path: "*",
        element: <ErrorBoundary notFound />,
      },
    ]
  },
]);

// Export default for backward compatibility
export default function Router() {
  return null; // This component is no longer used directly
}
